import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Divider,
  IconButton,
  Stack,
  Tooltip as MuiTooltip,
} from "@mui/material";
import { ActionButton, Button, Container, Icon, InputCheckbox, Text, Tooltip } from "@jds/core";
import avatar from "../../../../assets/png/avatarProfile.png";
import brokenImg from "../../../../assets/png/brokenImg.png";
import {
  addParamsURL,
  capitalizeWords,
  getLocalDate,
  getLocalTime,
} from "../../../../util/helper";
import mailIcon from "../../../../assets/svg/contact/ic-mail.svg";
import phoneIcon from "../../../../assets/svg/contact/ic-phone.svg";
import icPhoto from "../../../../assets/svg/action/ic-photo-white.svg";
import icCross from "../../../../assets/svg/action/ic-cross-white.svg";
import icCorrect from "../../../../assets/svg/action/ic-correct-white.svg";
import backGreyIcon from "../../../../assets/svg/action/back-grey.svg";
import calendarGreyIcon from "../../../../assets/svg/action/calendar-grey.svg";
import clockGreyIcon from "../../../../assets/svg/action/clock-grey.svg";
import GuestProfileReminderCTA from "./GuestProfileReminderCTA";
import decryptUrl from "../../../../util/img-decryption";
import "./GuestCards.css";

const PROFILE_PICTURE_REMINDER_ENABLED = true;

const statusMeta = {
  REGISTERED: { label: "Registered guest", icon: "IcTime", color: "feedback_warning_50" },
  APPROVED: { label: "Approved guest", icon: "IcSuccess", color: "success" },
  DENIED: { label: "Denied guest", icon: "IcError", color: "error" },
  BLOCKED: { label: "Blocked guest", icon: "IcBlocked", color: "primary_grey_80" },
  NULL: { label: "Invited guest", icon: "IcMail", color: "primary" },
};

const getStatus = (guest) => {
  if (guest?.isBlocked) return "BLOCKED";
  return guest?.registerStatus || "NULL";
};

const formatFieldValue = (value) => {
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return value || "-";
};

const getDecryptedValue = (value) => {
  if (!value) return "";
  try {
    return decryptUrl(value);
  } catch {
    return value;
  }
};

const CustomFieldRow = ({ customFields, field }) => {
  const fieldConfig = customFields?.[field?.id] || {};
  const question = fieldConfig?.questionName || "Custom field";
  const value = field?.value;

  if (fieldConfig?.type === "consent") {
    return (
      <Container gap={8} verticalAlign="center" fullWidth>
        <InputCheckbox checked={Boolean(value)} disabled />
        <Text text={question} appearance="body-s" color="primary_grey_100" />
      </Container>
    );
  }

  return (
    <Stack gap={0.5}>
      <Text text={question} appearance="body-xs-bold" color="primary_grey_80" />
      <Text text={formatFieldValue(value)} appearance="body-s" color="primary_grey_100" />
    </Stack>
  );
};

const AccessBadge = ({ children, color, icon }) => (
  <p className="guest-access-chip" style={{ backgroundColor: color }}>
    {icon}
    {children}
  </p>
);

const GuestAvatar = ({ guest, onFaceClick }) => {
  const [errorImg, setErrorImg] = useState(false);
  const status = getStatus(guest);
  const meta = statusMeta[status] || statusMeta.NULL;

  return (
    <Box position="relative" className={guest?.isBlocked ? "guest-avatar-muted" : ""}>
      <img
        className="guest-detail-avatar"
        src={
          errorImg
            ? brokenImg
            : guest?.profileFile
              ? URL.createObjectURL(guest?.profileFile)
              : guest?.profilePictureThumbnailUrl
                ? addParamsURL(guest?.profilePictureThumbnailUrl, { size: "s" })
                : avatar
        }
        alt={`${guest?.firstName || "Guest"} profile`}
        onClick={() => {
          if (guest?.profilePictureThumbnailUrl) onFaceClick();
        }}
        onError={() => setErrorImg(true)}
      />
      <MuiTooltip title={meta.label} arrow placement="bottom">
        <Box className="guest-detail-status-badge">
          <Icon ic={meta.icon} color={meta.color} />
        </Box>
      </MuiTooltip>
      {guest?.secondaryProfilesCount > 0 && (
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          className="secondary-profile-indicator guest-detail-secondary-count"
        >
          <Icon ic="IcProfile" size="s" color="white" />
          <p>{guest?.secondaryProfilesCount}</p>
        </Stack>
      )}
    </Box>
  );
};

const GuestViewingStatus = ({ guest }) => {
  if (!guest?.viewingStatus?.myPhotos?.visited) {
    return (
      <Container gap={6} verticalAlign="center" horizontalAlign="center">
        <Icon ic="IcVisible" color="grey-60" size="s" />
        <Text
          text="Not yet visited"
          appearance="body-s"
          color="primary_grey_80"
          className="guest-detail-viewing-status"
        />
      </Container>
    );
  }

  return (
    <Box
      sx={{
        "--color-primary-50": guest?.viewingStatus?.myPhotos?.hasPhotos
          ? "#25ab21"
          : "#ffd947ff",
      }}
    >
      <Container gap={6} verticalAlign="center" horizontalAlign="center">
        <Icon ic="IcVisible" size="s" />
        <Text
          text={
            guest?.viewingStatus?.myPhotos?.hasPhotos
              ? "Visited and viewed photos"
              : "Visited, but no photos were available at that time"
          }
          appearance="body-s"
          color="primary_grey_80"
          className="guest-detail-viewing-status"
        />
      </Container>
    </Box>
  );
};

const GuestDetailDrawer = ({
  open,
  onClose,
  guest,
  customFields = {},
  hasCustomField = false,
  setContactInfoModalClose = () => { },
  loadContactInfo,
  contactInfo = {},
  isContactInfoLoading = false,
  setIsFaceModalClose = () => { },
  onReminderClick = () => { },
  isReminderLoading = false,
  reminderForceDisabled = false,
  onBlock,
  onApprove,
  onDeny,
  onRestore,
  onEdit,
}) => {
  const [showFullContactInfo, setShowFullContactInfo] = useState(false);
  const status = getStatus(guest);
  const isInvited = status === "NULL";
  const isRegisteredLike = ["REGISTERED", "APPROVED", "DENIED", "BLOCKED"].includes(status);
  const fullEmail = contactInfo?.email ? getDecryptedValue(contactInfo?.email) : "";
  const fullPhoneNumber = contactInfo?.phoneNumber
    ? `${contactInfo?.countryCode || ""} ${getDecryptedValue(contactInfo?.phoneNumber)}`.trim()
    : "";
  const displayEmail = showFullContactInfo && fullEmail ? fullEmail : guest?.email;
  const displayPhoneNumber = showFullContactInfo && fullPhoneNumber
    ? fullPhoneNumber
    : guest?.phoneNumber;
  const hasLoadedFullContact = Boolean(fullEmail || fullPhoneNumber);

  useEffect(() => {
    if (open) {
      setShowFullContactInfo(false);
    }
  }, [guest?.id, open]);

  const handleViewFullContactInfo = () => {
    setShowFullContactInfo(true);
    (loadContactInfo || setContactInfoModalClose)(guest?.id);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ disablePortal: true }}
      sx={{ "& .MuiPaper-root": { borderRadius: 0 }, zIndex: 50 }}
    >
      <Box className="guest-detail-drawer" role="presentation">
        <Container
          fullWidth
          horizontalAlign="space-between"
          verticalAlign="center"
          className="guest-detail-actions"
          style={{ padding: '16px' }}
        >
          <IconButton className="guest-detail-back-button" onClick={onClose}>
            <img src={backGreyIcon} alt="Back" />
          </IconButton>
          <Container gap={8} verticalAlign="center" style={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
            {onBlock && (
              <Box sx={{ '& svg, & .text-atom': { color: '#000000a6' }, '& .j-JDSActionButton-inner-container:hover': { backgroundColor: '#0000000d' } }}>
                <ActionButton label="Block" icon="IcBlocked" kind="secondary" appearance="contrast" color={'primary-grey-80'} size="small" onClick={onBlock} />
              </Box>
            )}
            {onApprove && (
              <Button label="Approve" iconLeft="IcSuccess" kind="primary" state="positive" size="small" onClick={onApprove} />
            )}
            {onDeny && (
              <Button label="Deny" iconLeft="IcError" kind="primary" state="destructive" size="small" onClick={onDeny} />
            )}
            {onRestore && (
              <Button label={guest?.isBlocked ? "Unblock" : "Restore"} iconLeft="IcReset" kind="secondary" size="small" onClick={onRestore} />
            )}
          </Container>
        </Container>

        <Divider className="guest-detail-divider" />

        <Box className="guest-detail-content">
          <Stack alignItems="center" gap={1.5} pb={'36px'} borderBottom={'1px solid hsl(209deg 31.56% 90.52%)'}>
            <GuestAvatar guest={guest} onFaceClick={setIsFaceModalClose} />
            <Tooltip label={`${guest?.firstName || "User"} ${guest?.lastName || ""}`} position="bottom" sx={{ fontSize: '14px' }}>
              {/* <h2 className="guest-detail-name">
                {`${guest?.firstName || "User"} ${guest?.lastName || ""}`}
              </h2> */}
              <Text text={`${guest?.firstName || "User"} ${guest?.lastName || ""}`} appearance="heading-xs" />
            </Tooltip>
            <GuestViewingStatus guest={guest} />
            {onEdit && (
              <Button label="Edit" iconLeft="IcEdit" kind="secondary" size="small" onClick={onEdit} />
            )}
          </Stack>

          <Box className="guest-detail-section">
            <Text text="CONTACT DETAILS" appearance="body-s-bold" color="primary_grey_80" className="guest-detail-subheader" />
            <Stack gap={1.5} mt={1.5}>
              {displayEmail && (
                <Container gap={12} verticalAlign="center" fullWidth>
                  <span className="guest-detail-info-icon">
                    <img src={mailIcon} alt="" />
                  </span>
                  <Stack>
                    <Text text="Email" appearance="body-xs-bold" color="primary_grey_60" />
                    <Text text={displayEmail} appearance="body-s-bold" color="primary_grey_100" />
                  </Stack>
                </Container>
              )}
              {displayPhoneNumber && (
                <Container gap={12} verticalAlign="center" fullWidth>
                  <span className="guest-detail-info-icon">
                    <img src={phoneIcon} alt="" />
                  </span>
                  <Stack>
                    <Text text="Phone" appearance="body-xs-bold" color="primary_grey_60" />
                    <Text text={displayPhoneNumber} appearance="body-s-bold" color="primary_grey_100" />
                  </Stack>
                </Container>
              )}
              {showFullContactInfo && isContactInfoLoading && (
                <Text
                  text="Loading full contact info..."
                  appearance="body-s"
                  color="primary_grey_80"
                />
              )}
              {showFullContactInfo && !isContactInfoLoading && !hasLoadedFullContact && (
                <Text
                  text="No additional contact information available."
                  appearance="body-s"
                  color="primary_grey_80"
                />
              )}
              {!showFullContactInfo && (
                <Button
                  label="View full contact info"
                  kind="tertiary"
                  size="small"
                  onClick={handleViewFullContactInfo}
                />
              )}
            </Stack>
          </Box>

          {PROFILE_PICTURE_REMINDER_ENABLED &&
            guest?.reminderCtaState &&
            guest?.reminderCtaState !== "HIDDEN" && (
            <Box className="guest-detail-section" pt={0}>
              <GuestProfileReminderCTA
                reminderCtaState={guest?.reminderCtaState}
                onClick={() => onReminderClick(guest?.id)}
                isLoading={isReminderLoading}
                forceDisabled={reminderForceDisabled}
              />
            </Box>
          )}

          <Box className="guest-detail-section">
            <Text text="IMAGE ACCESS" appearance="body-s-bold" color="primary_grey_80" className="guest-detail-subheader" />
            <div className="guest-access-list">
              <AccessBadge
                color="var(--color-primary-50)"
                icon={<img src={icPhoto} alt="" />}
              >
                {guest?.access ? capitalizeWords(guest?.access, "_") : "No Access"}
              </AccessBadge>
              <AccessBadge
                color={
                  guest?.uploadAccess === "APPROVED"
                    ? "var(--color-feedback-success-50)"
                    : "var(--color-feedback-error-50)"
                }
                icon={<img src={guest?.uploadAccess === "APPROVED" ? icCorrect : icCross} alt="" />}
              >
                Upload Access
              </AccessBadge>
              <AccessBadge
                color={
                  guest?.downloadAccess === "APPROVED"
                    ? "var(--color-feedback-success-50)"
                    : "var(--color-feedback-error-50)"
                }
                icon={<img src={guest?.downloadAccess === "APPROVED" ? icCorrect : icCross} alt="" />}
              >
                Download Access
              </AccessBadge>
            </div>
          </Box>

          {(isInvited || isRegisteredLike) && (
            <Box className="guest-detail-section">
              <Text
                text={isInvited ? "INVITATION DETAILS" : "REGISTRATION DETAILS"}
                appearance="body-s-bold"
                color="primary_grey_80"
              />
              <Box className="guest-detail-date-grid">
                <Box className="guest-detail-date-card">
                  <img src={calendarGreyIcon} alt="" />
                  <Stack gap={0.5}>
                    <Text
                      text={isInvited ? "INVITED DATE" : "REGISTERED DATE"}
                      appearance="body-xs-bold"
                      color="primary_grey_80"
                    />
                    <Text
                      text={getLocalDate(guest?.createdAt)}
                      appearance="body-s-bold"
                      color="primary_grey_100"
                    />
                  </Stack>
                </Box>
                <Box className="guest-detail-date-card">
                  <img src={clockGreyIcon} alt="" />
                  <Stack gap={0.5}>
                    <Text
                      text={isInvited ? "INVITED TIME" : "REGISTERED TIME"}
                      appearance="body-xs-bold"
                      color="primary_grey_80"
                    />
                    <Text
                      text={getLocalTime(guest?.createdAt)}
                      appearance="body-s-bold"
                      color="primary_grey_100"
                    />
                  </Stack>
                </Box>
              </Box>
            </Box>
          )}

          <Box className="guest-detail-section">
            <Text text="GUEST INFORMATION" appearance="body-s-bold"
              color="primary_grey_80" />
            <Stack gap={1.5} mt={1.5}>
              {hasCustomField && guest?.customField?.length > 0 ? (
                guest.customField.map((item) => (
                  <CustomFieldRow key={item?.id} customFields={customFields} field={item} />
                ))
              ) : (
                <Text
                  text={guest?.customField ? "No custom field information available." : "Yet to register"}
                  appearance="body-s"
                  color="primary_grey_80"
                />
              )}
            </Stack>
          </Box>
        </Box>
      </Box>
    </Drawer >
  );
};

export default React.memo(GuestDetailDrawer);
